/**
 * Add or edit a member, with the duplicate check that runs before anything is
 * written.
 *
 * Name, phone and batch are required. The duplicate check looks for the same
 * phone as a member OR one of their guardians, or the same name, and opens a
 * side-by-side warning rather than refusing: a shared family phone is normal
 * and two players can share a name, so the person saving decides.
 *
 * Spec: DESIGN-BRIEF.md §C and `openMemberForm()` / `saveMember()` in
 * prototype.html.
 */
import React, {useMemo, useState} from 'react';
import {
  fmtDate,
  findDuplicate,
  todayISO,
  type Batch,
  type Guardian,
  type Member,
  type NetlyPayData,
} from '../../../internals/managers/NetlyPayManager';
import {GhostButton, Modal, PrimaryButton} from '../components/Overlays';

const inputCls =
  'w-full h-10 px-3 rounded-lg border border-borderc2 bg-surface text-[13.5px] text-text1 outline-none focus:border-brand transition-colors';
const labelCls = 'block text-[12.5px] font-medium text-text2 mb-1.5';

interface Draft {
  name: string;
  phone: string;
  email: string;
  dob: string;
  joined: string;
  batchId: string;
  guardians: Guardian[];
  custom: Record<string, string>;
}

const BLANK = (today: string): Draft => ({
  name: '',
  phone: '',
  email: '',
  dob: '',
  joined: today,
  batchId: '',
  guardians: [{name: '', relation: '', phone: ''}],
  custom: {jersey: '', school: '', medical: ''},
});

const CUSTOM_FIELDS: {key: string; label: string; placeholder?: string}[] = [
  {key: 'jersey', label: 'Jersey size', placeholder: 'YM'},
  {key: 'school', label: 'School', placeholder: 'Royal College'},
  {key: 'medical', label: 'Medical notes', placeholder: 'Mild asthma. Inhaler in bag.'},
];

interface Props {
  data: NetlyPayData;
  batches: Batch[];
  /** Null to add, a member to edit. */
  member?: Member | null;
  saving?: boolean;
  onCancel: () => void;
  onSave: (draft: Draft) => void;
}

const MemberFormModal = ({data, batches, member, saving, onCancel, onSave}: Props) => {
  const today = todayISO();
  const [draft, setDraft] = useState<Draft>(() =>
    member
      ? {
          name: member.name,
          phone: member.phone,
          email: member.email ?? '',
          dob: member.dob ?? '',
          joined: member.joined,
          batchId: member.batchId,
          guardians: member.guardians?.length
            ? member.guardians.map((g) => ({...g}))
            : [{name: '', relation: '', phone: ''}],
          custom: {...(member.custom ?? {})},
        }
      : BLANK(today),
  );
  const [touched, setTouched] = useState(false);
  const [duplicate, setDuplicate] = useState<Member | null>(null);

  const set = (patch: Partial<Draft>) => setDraft((d) => ({...d, ...patch}));

  const missing = useMemo(
    () => ({
      name: !draft.name.trim(),
      phone: !draft.phone.trim(),
      batchId: !draft.batchId,
    }),
    [draft],
  );
  const valid = !missing.name && !missing.phone && !missing.batchId;

  const setGuardian = (i: number, patch: Partial<Guardian>) =>
    set({guardians: draft.guardians.map((g, idx) => (idx === i ? {...g, ...patch} : g))});

  const commit = () =>
    onSave({
      ...draft,
      name: draft.name.trim(),
      // Rows left completely blank are not guardians.
      guardians: draft.guardians.filter((g) => g.name.trim() || g.phone.trim()),
    });

  const attemptSave = () => {
    if (!valid) {
      setTouched(true);
      return;
    }
    const dup = findDuplicate(data, {name: draft.name, phone: draft.phone}, member?.id ?? null);
    if (dup) {
      setDuplicate(dup);
      return;
    }
    commit();
  };

  if (duplicate) {
    const dupGuardian = duplicate.guardians?.[0];
    return (
      <Modal
        title="Someone like this is already a member"
        sub="Check the two side by side before adding another record"
        width={780}
        onClose={() => setDuplicate(null)}
        footer={
          <>
            <p className="text-[12.5px] text-text3">
              Adding both is fine when they are genuinely different people.
            </p>
            <span className="flex items-center gap-2.5">
              <GhostButton onClick={() => setDuplicate(null)}>Back</GhostButton>
              <PrimaryButton onClick={commit} disabled={saving}>
                {saving ? 'Saving…' : 'Create anyway'}
              </PrimaryButton>
            </span>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-borderc p-4">
            <p className="text-[11px] font-semibold tracking-[.06em] text-text3">ALREADY A MEMBER</p>
            <p className="text-[14px] font-semibold text-text1 mt-2">{duplicate.name}</p>
            <dl className="mt-3 flex flex-col gap-1.5 text-[12.5px]">
              <div className="flex justify-between gap-3">
                <dt className="text-text3">Member no.</dt>
                <dd className="font-mono text-text1">{duplicate.id}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-text3">Phone</dt>
                <dd className="font-mono text-text1">{duplicate.phone}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-text3">Guardian</dt>
                <dd className="text-text1">{dupGuardian ? dupGuardian.name : '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-text3">Joined</dt>
                <dd className="font-mono text-text1">{fmtDate(duplicate.joined)}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-xl border border-brand-soft bg-brand-soft/30 p-4">
            <p className="text-[11px] font-semibold tracking-[.06em] text-brand">BEING ADDED</p>
            <p className="text-[14px] font-semibold text-text1 mt-2">{draft.name.trim()}</p>
            <dl className="mt-3 flex flex-col gap-1.5 text-[12.5px]">
              <div className="flex justify-between gap-3">
                <dt className="text-text3">Member no.</dt>
                <dd className="font-mono text-text3">assigned on save</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-text3">Phone</dt>
                <dd className="font-mono text-text1">{draft.phone}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-text3">Guardian</dt>
                <dd className="text-text1">{draft.guardians[0]?.name || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-text3">Joined</dt>
                <dd className="font-mono text-text1">{fmtDate(draft.joined)}</dd>
              </div>
            </dl>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={member ? `Edit ${member.name}` : 'Add a member'}
      sub={member ? member.id : 'Name, phone and batch are needed'}
      width={720}
      onClose={onCancel}
      footer={
        <>
          <p className="text-[12.5px] text-text3">
            {touched && !valid ? 'Fill in the name, phone and batch.' : 'No request is sent yet.'}
          </p>
          <span className="flex items-center gap-2.5">
            <GhostButton onClick={onCancel}>Cancel</GhostButton>
            <PrimaryButton onClick={attemptSave} disabled={saving}>
              {saving ? 'Saving…' : member ? 'Save member' : 'Add member'}
            </PrimaryButton>
          </span>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelCls} htmlFor="np-m-name">
              Full name
            </label>
            <input
              id="np-m-name"
              value={draft.name}
              onChange={(e) => set({name: e.target.value})}
              className={`${inputCls} ${touched && missing.name ? 'border-neg' : ''}`}
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="np-m-phone">
              Phone
            </label>
            <input
              id="np-m-phone"
              value={draft.phone}
              onChange={(e) => set({phone: e.target.value})}
              placeholder="077 123 4501"
              className={`${inputCls} font-mono ${touched && missing.phone ? 'border-neg' : ''}`}
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="np-m-batch">
              Batch
            </label>
            <select
              id="np-m-batch"
              value={draft.batchId}
              onChange={(e) => set({batchId: e.target.value})}
              className={`nl-select ${touched && missing.batchId ? 'border-neg' : ''}`}
            >
              <option value="">Choose a batch</option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="np-m-email">
              Email <span className="text-text3 font-normal">optional</span>
            </label>
            <input
              id="np-m-email"
              value={draft.email}
              onChange={(e) => set({email: e.target.value})}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="np-m-dob">
              Date of birth <span className="text-text3 font-normal">optional</span>
            </label>
            <input
              id="np-m-dob"
              type="date"
              value={draft.dob}
              onChange={(e) => set({dob: e.target.value})}
              className={`${inputCls} font-mono`}
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="np-m-joined">
              Joined
            </label>
            <input
              id="np-m-joined"
              type="date"
              value={draft.joined}
              onChange={(e) => set({joined: e.target.value})}
              className={`${inputCls} font-mono`}
            />
          </div>
        </div>

        <div>
          <div className="flex items-baseline justify-between">
            <p className="text-[13px] font-semibold text-text1">Guardians</p>
            <button
              type="button"
              onClick={() => set({guardians: [...draft.guardians, {name: '', relation: '', phone: ''}]})}
              className="text-[12.5px] font-medium text-brand hover:underline"
            >
              Add another
            </button>
          </div>
          <p className="text-[11.5px] text-text3 mt-0.5">
            A guardian's phone is where reminders go for a member under 18.
          </p>
          <div className="flex flex-col gap-2.5 mt-3">
            {draft.guardians.map((g, i) => (
              <div key={i} className="grid gap-2.5 sm:grid-cols-[1.3fr_1fr_1.1fr_auto]">
                <input
                  value={g.name}
                  onChange={(e) => setGuardian(i, {name: e.target.value})}
                  placeholder="Name"
                  className={inputCls}
                  aria-label={`Guardian ${i + 1} name`}
                />
                <input
                  value={g.relation}
                  onChange={(e) => setGuardian(i, {relation: e.target.value})}
                  placeholder="Relation"
                  className={inputCls}
                  aria-label={`Guardian ${i + 1} relation`}
                />
                <input
                  value={g.phone}
                  onChange={(e) => setGuardian(i, {phone: e.target.value})}
                  placeholder="Phone"
                  className={`${inputCls} font-mono`}
                  aria-label={`Guardian ${i + 1} phone`}
                />
                <button
                  type="button"
                  onClick={() =>
                    set({
                      guardians:
                        draft.guardians.length > 1
                          ? draft.guardians.filter((_, idx) => idx !== i)
                          : [{name: '', relation: '', phone: ''}],
                    })
                  }
                  aria-label={`Remove guardian ${i + 1}`}
                  className="h-10 w-10 rounded-lg text-text3 hover:bg-surface2 hover:text-neg transition-colors"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[13px] font-semibold text-text1">Other details</p>
          <div className="grid gap-3 sm:grid-cols-3 mt-3">
            {CUSTOM_FIELDS.map((f) => (
              <div key={f.key}>
                <label className={labelCls} htmlFor={`np-m-${f.key}`}>
                  {f.label}
                </label>
                <input
                  id={`np-m-${f.key}`}
                  value={draft.custom[f.key] ?? ''}
                  onChange={(e) => set({custom: {...draft.custom, [f.key]: e.target.value}})}
                  placeholder={f.placeholder}
                  className={inputCls}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MemberFormModal;
